import React, { useState } from 'react';
import { useDealData } from 'contexts/DealContext';
import { usePortfolioData } from 'contexts/PortfolioContext';
import { useViewMode } from 'contexts/ViewModeContext';
import Card from 'components/card';
import { IoTrash, IoPencil, IoSave, IoClose } from 'react-icons/io5';
import { IoIosArrowRoundBack } from "react-icons/io";
import TooltipHorizon from 'components/tooltip';
import { getCurrentUser } from 'aws-amplify/auth';
import { Link } from 'react-router-dom';

const GroupCard = ({ group, username, onDelete, onUpdate, facilities, isPortfolioGroup = false }) => {
    const [isEditing, setIsEditing] = useState(false);
    const [editedName, setEditedName] = useState(group.name);
    const [editedPrivacy, setEditedPrivacy] = useState(group.isPrivate);
    const [selectedFacilities, setSelectedFacilities] = useState(group.facilityIds);

    const handleSave = async () => {
        const success = await onUpdate(group.id, {
            name: editedName.trim(),
            isPrivate: editedPrivacy,
            facilityIds: selectedFacilities
        });

        if (success) {
            setIsEditing(false);
        }
    };

    const handleCancel = () => {
        setEditedName(group.name);
        setEditedPrivacy(group.isPrivate);
        setSelectedFacilities(group.facilityIds);
        setIsEditing(false);
    };

    if (isEditing) {
        return (
            <Card key={group.id} extra="p-4">
                <div className="space-y-4">
                    {/* Group Name Edit */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Group Name
                        </label>
                        <input
                            type="text"
                            value={editedName}
                            onChange={(e) => setEditedName(e.target.value)}
                            className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                    </div>

                    {/* Privacy Toggle */}
                    <div>
                        <label className="flex items-center space-x-2">
                            <input
                                type="checkbox"
                                checked={editedPrivacy}
                                onChange={(e) => setEditedPrivacy(e.target.checked)}
                                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                            />
                            <span className="text-sm text-gray-700">Private Group</span>
                        </label>
                    </div>

                    {/* Facilities Selection */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Facilities
                        </label>
                        <div className="max-h-40 overflow-y-auto border border-gray-300 rounded-md p-2">
                            {facilities.map(facility => (
                                <label key={facility.id} className="flex items-center space-x-2 mb-2">
                                    <input
                                        type="checkbox"
                                        checked={selectedFacilities.includes(facility.id)}
                                        onChange={(e) => {
                                            if (e.target.checked) {
                                                setSelectedFacilities([...selectedFacilities, facility.id]);
                                            } else {
                                                setSelectedFacilities(selectedFacilities.filter(id => id !== facility.id));
                                            }
                                        }}
                                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                    />
                                    <span className="text-sm">{facility.name}</span>
                                </label>
                            ))}
                        </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex justify-end space-x-2">
                        <button
                            onClick={handleCancel}
                            className="px-3 py-1 text-sm text-gray-600 hover:bg-gray-100 rounded-md"
                        >
                            <IoClose size={20} />
                        </button>
                        <button
                            onClick={handleSave}
                            className="px-3 py-1 text-sm text-blue-600 hover:bg-blue-50 rounded-md"
                            disabled={!editedName.trim() || selectedFacilities.length === 0}
                        >
                            <IoSave size={20} />
                        </button>
                    </div>
                </div>
            </Card>
        );
    }

    return (
        <Card key={group.id} extra="p-4">
            <div className="flex justify-between items-start">
                <div>
                    <h4 className="text-lg font-medium">{group.name}</h4>
                    <p className="text-sm text-gray-700">
                        {group.isPrivate ? 'Private' : 'Public'} Group
                    </p>
                    <p className="text-sm text-gray-700">
                        Created by: {group.createdBy}
                    </p>
                    <p className="text-sm text-gray-700">
                        Facilities: {group.facilityIds.map(facility => facility).join(', ')}
                    </p>
                </div>
                {group.createdBy === username && (
                    <div className="flex gap-2">
                        <TooltipHorizon
                            content="Edit group"
                            placement="top"
                            trigger={
                                <button
                                    onClick={() => setIsEditing(true)}
                                    className="p-1 text-blue-600 hover:bg-blue-50 rounded-full"
                                    aria-label="Edit group"
                                >
                                    <IoPencil size={20} />
                                </button>
                            }
                        />
                        <TooltipHorizon
                            content="Delete group"
                            placement="top"
                            trigger={
                                <button
                                    onClick={() => {
                                        if (window.confirm('Are you sure you want to delete this group?')) {
                                            onDelete(group.id);
                                        }
                                    }}
                                    className="p-1 text-red-600 hover:bg-red-50 rounded-full"
                                    aria-label="Delete group"
                                >
                                    <IoTrash size={20} />
                                </button>
                            }
                        />
                    </div>
                )}
            </div>
        </Card>
    );
};

const ManageGroups = () => {
    const { groups, deleteGroup, updateGroup, consolidatedData } = useDealData();
    const {
        portfolioFacilityGroups,
        deletePortfolioGroup,
        updatePortfolioGroup,
        consolidatedPortfolioData
    } = usePortfolioData();
    const { viewMode } = useViewMode();
    const [username, setUsername] = React.useState(null);

    React.useEffect(() => {
        const fetchUsername = async () => {
            try {
                const user = await getCurrentUser();
                setUsername(user.username);
            } catch (error) {
                console.error('Error fetching username:', error);
            }
        };
        fetchUsername();
    }, []);

    // Get all facilities for each deal
    const dealFacilitiesMap = React.useMemo(() => {
        const map = {};
        // Return empty map if consolidatedData is not available
        if (!consolidatedData) return map;

        Object.entries(consolidatedData).forEach(([dealId, deal]) => {
            if (deal && deal.facilities) {
                map[dealId] = Object.entries(deal.facilities)
                    .filter(([name]) => name.toLowerCase() !== 'consolidated')
                    .map(([facilityName, facility]) => ({
                        id: facilityName,
                        name: facility.details?.Facilities || facilityName
                    }));
            }
        });
        return map;
    }, [consolidatedData]);

    // Get all facilities for each portfolio
    const portfolioFacilitiesMap = React.useMemo(() => {
        const map = {};
        // Return empty map if consolidatedPortfolioData is not available
        if (!consolidatedPortfolioData) return map;

        Object.entries(consolidatedPortfolioData).forEach(([portfolioId, portfolio]) => {
            if (portfolio && portfolio.facilities) {
                map[portfolioId] = Object.entries(portfolio.facilities)
                    .filter(([name]) => name.toLowerCase() !== 'consolidated')
                    .map(([facilityName, facility]) => ({
                        id: facilityName,
                        name: facility.details?.Facilities || facilityName
                    }));
            }
        });
        return map;
    }, [consolidatedPortfolioData]);

    // Group the groups by deal
    const groupsByDeal = React.useMemo(() => {
        // Make sure groups is an array before reducing
        if (!Array.isArray(groups)) return {};

        return groups.reduce((acc, group) => {
            if (!acc[group.dealId]) {
                acc[group.dealId] = [];
            }
            acc[group.dealId].push(group);
            return acc;
        }, {});
    }, [groups]);

    // Group the portfolio groups by portfolio
    const groupsByPortfolio = React.useMemo(() => {
        // Make sure portfolioFacilityGroups is an array before reducing
        if (!Array.isArray(portfolioFacilityGroups)) return {};

        return portfolioFacilityGroups.reduce((acc, group) => {
            if (!acc[group.portfolioId]) {
                acc[group.portfolioId] = [];
            }
            acc[group.portfolioId].push(group);
            return acc;
        }, {});
    }, [portfolioFacilityGroups]);

    return (
        <Card extra="w-full p-4">
            <div className="flex items-center gap-2">
                <p className="text-gray-700 text-sm dark:text-white">
                    <Link to={viewMode === 'portfolio' ? `/portfolio` : `/deals`} className="flex items-center gap-1">
                        <IoIosArrowRoundBack size={20} />
                        <span>Return to {viewMode === 'portfolio' ? 'Portfolio' : 'Deals'}</span>
                    </Link>
                </p>
            </div>
            <h3 className="ml-6 mb-4 mt-2 text-2xl self-start font-bold text-gray-700 dark:text-white">
                {viewMode === 'portfolio' ? 'Portfolio' : 'Facility'} Groups
            </h3>

            {/* Deal Groups */}
            {viewMode === 'deals' && (
                <>
                    {Object.entries(groupsByDeal).map(([dealId, groups]) => (
                        <div key={dealId} className="mb-8 ml-8">
                            <p className="text-xl text-gray-800 dark:text-gray-400">
                                {dealId}
                            </p>
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                {groups.map(group => (
                                    <GroupCard
                                        key={group.id}
                                        group={group}
                                        username={username}
                                        onDelete={deleteGroup}
                                        onUpdate={updateGroup}
                                        facilities={dealFacilitiesMap[dealId] || []}
                                        isPortfolioGroup={false}
                                    />
                                ))}
                            </div>
                        </div>
                    ))}

                    {Object.keys(groupsByDeal).length === 0 && (
                        <p className="text-gray-500 text-center py-8">
                            You haven't created any deal groups yet.
                        </p>
                    )}
                </>
            )}

            {/* Portfolio Groups */}
            {viewMode === 'portfolio' && (
                <>
                    {Object.entries(groupsByPortfolio).map(([portfolioId, groups]) => (
                        <div key={portfolioId} className="mb-8 ml-8">
                            <p className="text-xl text-gray-800 dark:text-gray-400">
                                {portfolioId}
                            </p>
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                {groups.map(group => (
                                    <GroupCard
                                        key={group.id}
                                        group={group}
                                        username={username}
                                        onDelete={deletePortfolioGroup}
                                        onUpdate={updatePortfolioGroup}
                                        facilities={portfolioFacilitiesMap[portfolioId] || []}
                                        isPortfolioGroup={true}
                                    />
                                ))}
                            </div>
                        </div>
                    ))}

                    {Object.keys(groupsByPortfolio).length === 0 && (
                        <p className="text-gray-500 text-center py-8">
                            You haven't created any portfolio groups yet.
                        </p>
                    )}
                </>
            )}
        </Card>
    );
};

export default ManageGroups; 